import { type Method } from "axios";

type StringMatcher = string | RegExp;

type UppercaseMethod = Uppercase<Method>;

interface BaseRule {
  method: UppercaseMethod | UppercaseMethod[];
  url: StringMatcher | StringMatcher[];
  status: number | number[];
}

type IgnoredApiErrorRule =
  | (BaseRule & { code: string | string[]; message?: StringMatcher | StringMatcher[] })
  | (BaseRule & { code?: string | string[]; message: StringMatcher | StringMatcher[] });

interface ApiErrorContext {
  method: string;
  url: string;
  status: number | string;
  codes: string[];
  message: string;
}

const IGNORED_API_ERRORS: IgnoredApiErrorRule[] = [
  {
    method: "POST",
    url: "/api/v1/employees/login",
    status: 403,
    message: "Invalid login or password",
  },
  {
    method: "POST",
    url: "/api/v1/candidates",
    status: 422,
    code: "candidate_already_exists",
  },
  {
    method: "POST",
    url: ["/api/v1/employees/tfa/setup-verify", "/api/v1/employees/tfa/verify"],
    status: 422,
    code: "tfa_wrong_code",
  },
];

const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

const stripQuery = (url: string): string => url.split(/[?#]/)[0];

const matchUrl = (matcher: StringMatcher, value: string): boolean => {
  const pathname = stripQuery(value);
  return matcher instanceof RegExp ? matcher.test(pathname) : matcher === pathname;
};

const matchMessage = (matcher: StringMatcher, value: string): boolean =>
  matcher instanceof RegExp ? matcher.test(value) : matcher !== "" && value.includes(matcher);

const matchField = <T>(rule: T | T[], test: (item: T) => boolean): boolean =>
  toArray(rule).some(test);

const matchOptionalField = <T>(rule: T | T[] | undefined, test: (item: T) => boolean): boolean =>
  rule === undefined || toArray(rule).some(test);

const matchesRule = (rule: IgnoredApiErrorRule, ctx: ApiErrorContext): boolean =>
  matchField(rule.method, (m) => m === ctx.method) &&
  matchField(rule.url, (u) => matchUrl(u, ctx.url)) &&
  matchField(rule.status, (s) => s === ctx.status) &&
  matchOptionalField(rule.code, (c) => ctx.codes.includes(c)) &&
  matchOptionalField(rule.message, (msg) => matchMessage(msg, ctx.message));

export const isIgnoredApiError = (ctx: ApiErrorContext): boolean =>
  IGNORED_API_ERRORS.some((rule) => matchesRule(rule, ctx));
